<template>
  <component
    :is="maskShape ? BaseShapeMask : 'div'"
    :shape="maskShape"
    :stroke="maskShape && highContrast ? { width: 6, color: getColorForMember + '-30' } : undefined"
    v-tooltip="omitTooltip ? undefined : memberFullName(member)"
  >
    <div
      class="flex shrink-0 grow-0 items-center justify-center overflow-hidden rounded-full uppercase select-none"
      :class="[
        sizeClasses,
        `bg-${getColorForMember}-90 text-${getColorForMember}-30 dark:bg-${getColorForMember}-30 dark:text-${getColorForMember}-90`,
        highContrast && !maskShape && `border-${getColorForMember}-30 border-2`,
      ]"
    >
      <img v-if="hasImage" class="h-full w-full" :src="avatarUrl!" alt="" />
      <span v-else>
        <template v-if="includeLastName">{{ memberInitials(member) }}</template>
        <template v-else>{{ memberInitials(member)[0] }}</template>
      </span>
    </div>
  </component>
</template>
<script lang="ts" setup>
  import { computed } from 'vue'
  import { useAvatarStore } from '../../../../stores/avatar.store'
  import type { AvatarMemberAttributes, AvatarSize } from './avatar-props'
  import { useColorForMember } from '../../../composables/useColorForMember'
  import { memberFullName, memberInitials } from '@/core/models/member/member-name'
  import type { Shape } from '../shapes/shape-paths'
  import BaseShapeMask from '../shapes/BaseShapeMask.vue'

  interface Props {
    member: AvatarMemberAttributes
    includeLastName?: boolean
    size?: AvatarSize
    omitTooltip?: boolean
    highContrast?: boolean
    maskShape?: Shape
  }

  const { size = '40', member } = defineProps<Props>()

  const avatarStore = useAvatarStore()

  const { getColorForMember } = useColorForMember(member)

  const sizeClasses = computed(() => {
    if (size == '160') {
      return 'h-40 w-40 text-display-large'
    }
    if (size == '128') {
      return 'h-32 w-32 text-title-large'
    }
    if (size == '96') {
      return 'h-24 w-24 text-title-large'
    }
    if (size == '80') {
      return 'h-20 w-20 text-headline-large'
    }
    if (size == '64') {
      return 'h-16 w-16 text-title-large'
    }
    if (size == '56') {
      return 'h-14 w-14 text-title-large'
    }
    if (size == '32') {
      return 'h-8 w-8 text-label-large'
    }
    if (size == '24') {
      return 'h-6 w-6 text-label-small'
    }
    return 'h-10 w-10 text-label-large'
  })

  const avatarUrl = computed(() => {
    if (member.avatarFileContent) {
      return `data:image/png;base64,${member.avatarFileContent}`
    }
    return avatarStore.getAvatarForMember(member.id, member.lastUpdatedAvatar).value
  })

  const hasImage = computed(() => member.lastUpdatedAvatar && avatarUrl.value)
</script>
